/**
 * FAQ Section Web Component
 * Dynamically builds navigation from heading blocks within accordion blocks and handles smooth scrolling
 *
 * @class
 * @extends HTMLElement
 */
class FAQSection extends HTMLElement {
  #navContainer = null;
  #contentArea = null;
  #navList = null;
  #navLinks = new Map();
  #slideIndices = new Map();
  #headings = [];
  #activeObserver = null;
  #scrollTimeout = null;
  #navClickHandler = null;
  #swiper = null;
  #swiperElement = null;
  #activeClass = null;
  #inactiveClass = null;

  // Constants
  static #SCROLL_DELAY = 1000;
  static #SCROLL_OFFSET = 20;
  static #OBSERVER_TOP_MARGIN = 100;
  static #MIN_HEADINGS_FOR_NAV = 2;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  /**
   * Initialize the FAQ section component
   */
  #init() {
    if (!this.#setupElements()) return;

    this.#headings = this.#findHeadingsInAccordions();
    if (this.#headings.length < FAQSection.#MIN_HEADINGS_FOR_NAV) {
      this.#navContainer.style.display = 'none';
      return;
    }

    this.#addAnchorIdsToHeadings();
    this.#buildNavigation();
    this.#setupNavLinks();
    this.#initSwiper();
    this.#handleInitialHash();
    this.#setupIntersectionObserver();
  }

  /**
   * Setup and cache DOM elements
   * @returns {boolean} True if all required elements are found
   */
  #setupElements() {
    this.#navContainer = this.querySelector('[data-faq-navigation]');
    this.#swiperElement = this.#navContainer?.querySelector('[data-faq-navigation-swiper]');
    this.#navList = this.#swiperElement?.querySelector('ul');
    this.#contentArea = this.querySelector('[data-faq-content]');

    if (!this.#navList || !this.#contentArea) return false;

    this.#activeClass = this.#navContainer.getAttribute('data-active-class') || 'button--black';
    this.#inactiveClass =
      this.#navContainer.getAttribute('data-inactive-class') || 'button--neutral';
    return true;
  }

  /**
   * Find all headings within accordion blocks
   * @returns {HTMLElement[]} Array of heading elements
   */
  #findHeadingsInAccordions() {
    const accordions = this.#contentArea.querySelectorAll('accordion-element');
    return Array.from(accordions).flatMap((accordion) =>
      Array.from(accordion.querySelectorAll('[data-heading]'))
    );
  }

  /**
   * Handle initial page hash or set first item as active
   */
  #handleInitialHash() {
    const hash = window.location.hash.substring(1);
    if (!hash) this.#setFirstItemActive();
    else this.#scrollToAnchor(hash);
  }

  /**
   * Set the first navigation item as active
   */
  #setFirstItemActive() {
    const firstAnchorId = this.#headings[0]?.id;
    if (firstAnchorId) {
      this.#updateActiveNav(firstAnchorId);
    }
  }

  /**
   * Generate a URL-friendly anchor ID from text
   */
  #generateAnchorId(text) {
    return `faq-${text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')}`;
  }

  /**
   * Add anchor IDs to all headings that don't already have one
   */
  #addAnchorIdsToHeadings() {
    this.#headings.forEach((heading) => {
      if (heading.id) return;

      const text = heading.textContent.trim();
      if (!text) return;

      const anchorId = this.#generateAnchorId(text);
      heading.id = anchorId;
      heading.setAttribute('data-faq-heading', '');
    });
  }

  /**
   * Build the navigation menu from headings
   */
  #buildNavigation() {
    this.#navList.innerHTML = '';
    this.#navLinks.clear();
    this.#slideIndices.clear();

    const fragment = document.createDocumentFragment();
    this.#headings.forEach((heading, index) => {
      const anchorId = heading.id || this.#generateAnchorId(heading.textContent.trim());
      const headingText = heading.textContent.trim();
      if (!anchorId || !headingText) return;

      const link = this.#createNavLink(anchorId, headingText);
      fragment.appendChild(link);
      this.#navLinks.set(anchorId, link.querySelector('a'));
      this.#slideIndices.set(anchorId, index);
    });

    this.#navList.appendChild(fragment);
  }

  /**
   * Create a navigation link element
   */
  #createNavLink(anchorId, text) {
    const listItem = document.createElement('li');
    listItem.className = 'swiper-slide max-w-fit';
    listItem.innerHTML = `
      <a href="#${anchorId}"
        class="button button--solid button--small ${this.#inactiveClass}"
         data-nav-anchor="${anchorId}">
        ${text}
      </a>
    `;
    return listItem;
  }

  /**
   * Set up click event handler for navigation links
   */
  #setupNavLinks() {
    this.#navClickHandler = (event) => {
      const link = event.target.closest('a[data-nav-anchor]');
      if (!link) return;

      event.preventDefault();
      this.#scrollToAnchor(link.getAttribute('data-nav-anchor'));
    };
    this.#navList.addEventListener('click', this.#navClickHandler);
  }

  /**
   * Get the header offset for scroll calculations
   */
  #getHeaderOffset(additionalOffset = 0) {
    return (window.HEADER_HEIGHT || 0) + additionalOffset;
  }

  /**
   * Scroll to a specific anchor and update navigation state
   */
  #scrollToAnchor(anchorId) {
    const targetElement = document.getElementById(anchorId);
    if (!targetElement) return;

    this.#disconnectObserver();
    const offset = this.#getHeaderOffset(FAQSection.#SCROLL_OFFSET);
    const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    if (history.pushState) {
      history.pushState(null, null, `#${anchorId}`);
    }
    this.#updateActiveNav(anchorId);
    this.#scrollTimeout = setTimeout(
      () => this.#setupIntersectionObserver(),
      FAQSection.#SCROLL_DELAY
    );
  }

  /**
   * Find the most visible entry from intersection observer entries
   */
  #findMostVisibleEntry(entries) {
    return entries
      .filter((entry) => entry.isIntersecting)
      .reduce(
        (mostVisible, entry) =>
          !mostVisible || entry.intersectionRatio > mostVisible.intersectionRatio
            ? entry
            : mostVisible,
        null
      );
  }

  /**
   * Set up intersection observer to track which heading is currently visible
   */
  #setupIntersectionObserver() {
    this.#disconnectObserver();
    const topMargin = this.#getHeaderOffset(FAQSection.#OBSERVER_TOP_MARGIN);

    this.#activeObserver = new IntersectionObserver(
      (entries) => {
        const mostVisible = this.#findMostVisibleEntry(entries);
        if (!mostVisible) return;

        const anchorId = mostVisible.target.id;
        if (anchorId) {
          this.#updateActiveNav(anchorId);
        }
      },
      {
        root: null,
        rootMargin: `-${topMargin}px 0px -50% 0px`,
        threshold: 0,
      }
    );

    this.#headings.forEach((heading) => this.#activeObserver.observe(heading));
  }

  /**
   * Disconnect and clean up the intersection observer
   */
  #disconnectObserver() {
    if (this.#activeObserver) {
      this.#activeObserver.disconnect();
      this.#activeObserver = null;
    }
  }

  /**
   * Update CSS classes and ARIA attributes for a navigation link
   */
  #updateLinkClasses(link, isActive) {
    if (isActive) {
      link.classList.replace(this.#inactiveClass, this.#activeClass);
      link.setAttribute('aria-current', 'location');
    } else {
      link.classList.replace(this.#activeClass, this.#inactiveClass);
      link.removeAttribute('aria-current');
    }
  }

  /**
   * Scroll Swiper to show the active navigation link
   */
  #scrollSwiperToActive(anchorId) {
    if (!this.#swiper) return;

    const slideIndex = this.#slideIndices.get(anchorId);
    if (slideIndex !== undefined) {
      this.#swiper.slideTo(slideIndex, 300);
    }
  }

  /**
   * Update active navigation state based on current anchor
   */
  #updateActiveNav(anchorId) {
    this.#navLinks.forEach((link, linkAnchorId) => {
      const isActive = linkAnchorId === anchorId;
      this.#updateLinkClasses(link, isActive);
      if (isActive) {
        this.#scrollSwiperToActive(anchorId);
      }
    });
  }

  /**
   * Initialize Swiper instance for mobile navigation
   */
  async #initSwiper() {
    if (!this.#swiperElement) return;

    try {
      const { Swiper } = await import('@services');
      this.#swiper = new Swiper(this.#swiperElement, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        breakpoints: {
          768: { enabled: false },
        },
      });
    } catch (error) {
      console.error('Error initializing FAQ Swiper:', error);
    }
  }

  /**
   * Clean up all resources
   */
  #cleanup() {
    this.#disconnectObserver();
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }
    if (this.#navClickHandler && this.#navList) {
      this.#navList.removeEventListener('click', this.#navClickHandler);
    }
    this.#destroySwiper();
  }

  /**
   * Destroy Swiper instance and clean up resources
   */
  #destroySwiper() {
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }
}

customElements.define('faq-section', FAQSection);
